import React, { useEffect, useState } from "react";
import { Mail, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";

const TRIGGER_LABELS = {
  user_created: "New User Welcome",
  password_reset: "Password Reset",
  allotment_confirmed: "Delegate Allotment Confirmation",
  principal_registered: "Principal Account Created",
  meeting_invite: "Meeting Invitation",
  query_raised: "Query Raised (PM Notification)",
  task_assigned: "Task Assigned",
  task_message_posted: "Task — New Message Posted",
  task_deadline_48h: "Task — Deadline in 48 Hours",
  chat_message_received: "Chat — New Message Received",
  social_post_remark: "Social Post — New Remark",
  social_post_design_link: "Social Post — Design Link Added",
  printables_added: "Design/Printable — Added or Submitted",
  printables_remark: "Design/Printable — New Remark",
  blog_submitted_to_head: "Blog — Submitted to Head of M&C",
  blog_returned_to_writer: "Blog — Returned to Writer",
  blog_reviewed: "Blog — Reviewed by Head",
  blog_stale: "Blog — Stale (No Review)",
  school_meeting_scheduled: "School Meeting Scheduled (Principal Notice)",
};

const DEFAULT_HTML = (subject) => `<div style="font-family: 'Outfit', sans-serif; max-width: 560px; margin: 0 auto; background: white; border-radius: 16px; overflow: hidden;">
  <div style="background: linear-gradient(135deg,#F97316,#FBBF24); padding: 32px; text-align: center;">
    <img src="{{LOGO_URL}}" alt="MUNner's" style="height: 40px;" />
    <h1 style="color: white; font-size: 28px; margin: 16px 0 0;">${subject}</h1>
  </div>
  <div style="padding: 32px; color: #0F172A; line-height: 1.6;">
    <p>Hi {{NAME}},</p>
    <p>Your message body goes here.</p>
    <a href="{{LINK_URL}}" style="display:inline-block; background:#F97316; color:white; padding:12px 24px; border-radius:9999px; text-decoration:none; font-weight:bold;">{{LINK_TEXT}}</a>
  </div>
  <div style="padding: 16px 32px; background: #F8FAFC; color:#64748B; font-size:12px;">— Team MUNner's</div>
</div>`;

export default function EmailTemplatesAdmin() {
  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/email-templates");
    setTemplates(r.data);
    if (!active && r.data[0]) setActive(r.data[0]);
  };
  useEffect(() => { load(); }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd);
    setActive({ ...active, logo_url: `${BACKEND_URL}${r.data.url}` });
  };

  const save = async () => {
    if (!active) return;
    await api.put(`/admin/email-templates/${active.trigger_key}`, active);
    toast.success("Template saved");
    load();
  };

  const reset = () => {
    if (!active) return;
    setActive({ ...active, html_body: DEFAULT_HTML(active.subject || TRIGGER_LABELS[active.trigger_key]) });
  };

  if (!active) return <div className="p-12 text-slate-400">Loading…</div>;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-display font-bold text-xl">Email Templates</h3>
        <div className="flex gap-2">
          <button onClick={reset} className="btn-ghost text-sm" data-testid="email-reset"><RotateCcw className="w-4 h-4" /> Insert sample HTML</button>
          <button onClick={save} className="btn-primary text-sm" data-testid="email-save"><Save className="w-4 h-4" /> Save template</button>
        </div>
      </div>
      <div className="grid lg:grid-cols-4 gap-5">
        <div className="dashboard-card p-3 h-fit">
          {templates.map((t) => (
            <button key={t.trigger_key} onClick={() => setActive(t)} className={`w-full text-left p-3 rounded-xl mb-1 text-sm flex items-center gap-2 ${active.trigger_key === t.trigger_key ? "bg-orange-50 text-orange-700" : "hover:bg-slate-50"}`} data-testid={`email-trigger-${t.trigger_key}`}>
              <Mail className="w-4 h-4" />
              <span className="flex-1 truncate">{TRIGGER_LABELS[t.trigger_key] || t.trigger_key}</span>
              {t.html_body && <span className="w-2 h-2 rounded-full bg-emerald-500" />}
            </button>
          ))}
        </div>

        <div className="lg:col-span-3 dashboard-card p-5">
          <div className="space-y-3">
            <input placeholder="Subject" className="input-pill" value={active.subject || ""} onChange={(e) => setActive({ ...active, subject: e.target.value })} data-testid="email-subject" />
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Logo URL</label>
                <div className="flex gap-2 mt-1">
                  <input placeholder="https://…" className="input-pill flex-1" value={active.logo_url || ""} onChange={(e) => setActive({ ...active, logo_url: e.target.value })} />
                  <label className="btn-ghost px-3 cursor-pointer">Upload<input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} /></label>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500">Action link URL</label>
                <input placeholder="https://…" className="input-pill mt-1" value={active.link_url || ""} onChange={(e) => setActive({ ...active, link_url: e.target.value })} />
              </div>
            </div>
            <input placeholder="Action button text" className="input-pill" value={active.link_text || ""} onChange={(e) => setActive({ ...active, link_text: e.target.value })} />
            <div>
              <label className="text-xs text-slate-500">Available placeholders for <b>{active.trigger_key}</b>:</label>
              <div className="flex flex-wrap gap-1 mt-1 mb-2">
                {(active.available_vars || []).map((v) => (
                  <button key={v} type="button" onClick={() => {
                    const tag = "{{" + v + "}}";
                    setActive({ ...active, html_body: (active.html_body || "") + tag });
                  }} className="text-[10px] bg-slate-100 hover:bg-orange-100 hover:text-orange-700 px-2 py-1 rounded-full font-mono cursor-pointer" data-testid={`var-${v}`} title="Click to insert into body">
                    {"{{" + v + "}}"}
                  </button>
                ))}
              </div>
              <textarea rows={12} className="input-pill mt-1 font-mono text-xs" value={active.html_body || ""} onChange={(e) => setActive({ ...active, html_body: e.target.value })} data-testid="email-html" />
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-2">Preview (placeholders are filled with sample values)</div>
              <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
                <iframe title="preview" srcDoc={renderPreview(active)} className="w-full h-72 bg-white rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Replace any {{var}} placeholders with friendly sample data for preview
function renderPreview(t) {
  const samples = {
    name: "Sanyam Jain", delegate_name: "Sanyam Jain",
    email: "sanyam@example.com", role: "Project Manager",
    temp_password: "X9zKpQ-aB", link_url: t.link_url || "#",
    link_text: t.link_text || "Open MUNner's", logo_url: t.logo_url || "",
    committee: "UNGA", portfolio: "Australia",
    event_name: "KAIMERA MUN 2026", event_dates: "10 Oct → 11 Oct",
    school_name: "Alliance International School",
    payment_status: "PAID", paid: "Yes",
    qr_token: "QR-ABC123XYZ",
    qr_image: '<img src="https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=QR-ABC123XYZ" style="width:180px;height:180px;border:1px solid #eee;border-radius:8px"/>',
    checkin_url: "https://munners.app/checkin?qr=QR-ABC123XYZ",
    host: "Regional Manager", title: "Strategy Sync",
    scheduled_at: "2026-06-15 10:00 UTC",
    meeting_link: "https://meet.google.com/abc-defg-hij",
    meet_link: "https://meet.google.com/abc-defg-hij",
    duration_min: "30",
    from_name: "Principal — Alliance Int'l", to_name: "Regional Manager",
    subject: "Question about delegate count", body: "Could we add 20 more delegates?",
    task_title: "Prepare proposal", due_date: "2026-06-20", assigned_by: "Regional Manager",
    // Feb 2026 cross-cutting notifications
    recipient_name: "Sanyam Jain", recipient_email: "sanyam@example.com",
    deadline: "2026-06-20 18:00",
    sender_name: "Regional Manager", message_preview: "Please make sure the deck is ready by EOD.",
    thread_name: "Bengaluru Ops",
    post_title: "Delegate Registration Live", author_name: "SM Manager",
    remark_text: "Please add a call-to-action button in the caption.",
    designer_name: "Design Lead", design_link: "https://drive.google.com/file/d/EXAMPLE/view",
    design_note: "Final round of revisions attached.",
    printable_name: "Allotment Certificate — UNGA",
    description: "A4 landscape certificate with school header + QR footer.",
    added_by: "Project Manager", link: "https://drive.google.com/file/d/DESIGN123/view", label: "Draft v1",
    blog_title: "Why MUNs Build Better Leaders",
    reviewer_name: "Head of M&C", outcome: "approved",
    note: "Please tighten the intro and re-check citations.",
    submitted_at: "2026-06-13 09:12 UTC",
    scheduler_name: "Regional Manager", when: "2026-06-18 15:30",
    location: "Alliance International School — Principal's Office",
  };
  let html = t.html_body || "";
  for (const [k, v] of Object.entries(samples)) {
    html = html.split("{{" + k + "}}").join(String(v));
  }
  // Legacy placeholders
  html = html.replaceAll("{{NAME}}", samples.name).replaceAll("{{LOGO_URL}}", samples.logo_url).replaceAll("{{LINK_URL}}", samples.link_url).replaceAll("{{LINK_TEXT}}", samples.link_text);
  return html;
}
